import React from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

function Shell({ title, updated, children, testid }) {
  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-14" data-testid={testid}>
        <h1 className="font-display text-4xl font-bold mb-2">{title}</h1>
        <p className="text-zinc-500 text-sm mb-10">Last updated: {updated}</p>
        <div className="space-y-6 text-zinc-300 leading-relaxed text-[15px]">{children}</div>
      </div>
      <Footer />
    </div>
  );
}

const H = ({ children }) => <h2 className="font-display text-xl font-semibold text-white mt-8 mb-2">{children}</h2>;

export function Privacy() {
  return (
    <Shell title="Privacy Policy" updated="June 2026" testid="privacy-page">
      <p>This Privacy Policy explains how Clipstar ("we", "us") collects, uses and protects your information when you use our service.</p>
      <H>Information we collect</H>
      <p>Account information (name, email, hashed password), campaigns you save or import, videos you upload for processing, generated clips, subscription and billing status, and usage/diagnostic data needed to operate the service.</p>
      <H>How we use it</H>
      <p>To provide campaign discovery, process your videos into clips, manage your subscription and credits, secure your account, and improve the product. We do not sell your personal data.</p>
      <H>Payments</H>
      <p>Payments are processed by Stripe. We never store your full card details. Stripe processes card data under its own privacy terms.</p>
      <H>Storage</H>
      <p>Uploaded videos and generated clips are stored in private object storage and are accessible only to your authenticated account. We do not make your media public.</p>
      <H>Your rights</H>
      <p>You may request access to, correction of, or deletion of your personal data by contacting support. Deleting your account removes your private media and personal data.</p>
      <H>Contact</H>
      <p>For privacy requests, contact support@clipstar.app.</p>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service" updated="June 2026" testid="terms-page">
      <p>By using Clipstar you agree to these Terms. Clipstar is a tool for people who participate in clipping campaigns hosted on third-party platforms.</p>
      <H>What Clipstar is</H>
      <p>Clipstar lets you discover, import and save publicly available campaign information for free, and provides paid AI tools to create campaign-compliant clips from footage you upload or supply. Clipstar is not a campaign marketplace and is not affiliated with any campaign platform.</p>
      <H>Your responsibilities</H>
      <p>You must own or have the rights to any footage you upload. You must comply with each campaign's rules and each platform's terms. You must not use Clipstar to infringe copyright, bypass access controls, or process content you are not authorised to use.</p>
      <H>Acceptable use</H>
      <p>You may not attempt to bypass authentication, paywalls, CAPTCHA, DRM or access controls of any platform. Where a campaign cannot be imported automatically, you may enter its details manually.</p>
      <H>Subscriptions &amp; credits</H>
      <p>Paid video-processing features require an active subscription. Subscriptions are billed on a recurring monthly basis until cancelled. Credits are consumed by processing operations and are subject to your plan.</p>
      <H>Availability</H>
      <p>The service is provided "as is". We do not guarantee that any clip will meet a specific campaign's approval; the compliance engine is a best-effort aid.</p>
      <H>Contact</H>
      <p>Questions about these Terms: support@clipstar.app.</p>
    </Shell>
  );
}

export function Refunds() {
  return (
    <Shell title="Billing, Cancellation &amp; Refunds" updated="June 2026" testid="refunds-page">
      <H>Recurring billing</H>
      <p>Paid plans (Starter, Creator, Pro, Agency) are subscriptions billed automatically every month to your payment method via Stripe until you cancel. The price and included monthly credits for each plan are shown on the Pricing page before you subscribe.</p>
      <H>Monthly credits</H>
      <p>Each billing cycle allocates the credits included in your plan. Credits are used only for paid video-processing operations — never for discovering, importing, saving or viewing campaigns.</p>
      <H>Cancellation</H>
      <p>You can cancel at any time from your account. Cancellation stops future renewals; your paid processing tools remain available until the end of the current period, after which your account returns to the free tier. Your saved and imported campaigns always remain available for free.</p>
      <H>Refunds</H>
      <p>Because subscriptions unlock immediate access to processing capacity, monthly fees are generally non-refundable once a billing cycle has started. If you were charged in error or experienced a service failure, contact support@clipstar.app and we will review your case.</p>
      <H>Failed payments</H>
      <p>If a renewal payment fails, your subscription is marked past-due and paid processing is suspended until payment succeeds. Your campaign library remains accessible.</p>
    </Shell>
  );
}
